import { ToastService } from '@agroone-front/shared'
import { HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { TranslateService } from '@ngx-translate/core'
import { catchError, debounceTime, map, mergeMap, of } from 'rxjs'
import { FieldHttpParams, FieldService } from '../../../../../shared/field/services/field.service'
import {
  addField,
  addFieldFailure,
  addFieldSuccess,
  deleteField,
  deleteFieldError,
  deleteFieldSuccess,
  loadFields,
  loadFieldsFailure,
  loadFieldsSuccess,
  updateField,
  updateFieldFailure,
  updateFieldSuccess,
} from '../actions/field.actions'

@Injectable()
export class FieldEffects {
  private actions$ = inject(Actions)
  private fieldService = inject(FieldService)
  private toastService = inject(ToastService)
  private translate = inject(TranslateService)

  public getFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFields),
      debounceTime(100),
      mergeMap((params: FieldHttpParams) =>
        this.fieldService.getAll(params).pipe(
          map((fields) => loadFieldsSuccess({ fields })),
          catchError((e) => of(loadFieldsFailure({ error: `${e}` }))),
        ),
      ),
    ),
  )

  public deleteField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteField),
      mergeMap(({ fieldId }) =>
        this.fieldService.delete(fieldId).pipe(
          map(() => {
            this.toastService.showToastSuccess(this.translate.instant('FIELD.DELETE_SUCCESS'))
            return deleteFieldSuccess({ payload: { fieldId } })
          }),
          catchError((e) => {
            this.toastService.showToastError(this.translate.instant('FIELD.DELETE_FAILED'))
            return of(deleteFieldError({ error: `${e}` }))
          }),
        ),
      ),
    ),
  )

  public addField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addField),
      mergeMap(({ payload }) =>
        this.fieldService.save(payload.field).pipe(
          map((field) =>
            addFieldSuccess({
              payload: {
                field,
                loading: false,
              },
            }),
          ),
          catchError((e) =>
            of(
              addFieldFailure({
                error: `${e}`,
                status: e instanceof HttpErrorResponse ? e.status : undefined,
                loading: false,
              }),
            ),
          ),
        ),
      ),
    ),
  )

  public updateField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateField),
      mergeMap(({ payload }) =>
        this.fieldService.update(payload.field).pipe(
          map((field) =>
            updateFieldSuccess({
              payload: {
                field,
                loading: false,
              },
            }),
          ),
          catchError((e) =>
            of(
              updateFieldFailure({
                error: `${e}`,
                status: e instanceof HttpErrorResponse ? e.status : undefined,
                loading: false,
              }),
            ),
          ),
        ),
      ),
    ),
  )
}
